
import { useEffect, useRef } from 'react';
import { $getRoot } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { $generateHtmlFromNodes } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createListNode, $createListItemNode } from '@lexical/list';
import { $createLinkNode } from '@lexical/link';
import { $setBlocksType } from '@lexical/selection';
import { $getSelection as getSelection, $isRangeSelection } from 'lexical';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string, html?: string) => void;
  placeholder?: string;
  className?: string;
}

const theme = {
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
  heading: {
    h1: 'text-2xl font-bold mb-2',
    h2: 'text-xl font-bold mb-2',
    h3: 'text-lg font-bold mb-2',
  },
  list: {
    nested: {
      listitem: 'list-none',
    },
    ol: 'list-decimal list-inside',
    ul: 'list-disc list-inside',
  },
  quote: 'border-l-4 border-gray-300 pl-4 italic text-gray-600',
  link: 'text-blue-600 hover:text-blue-800 underline',
};

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  const formatText = (format: string) => {
    editor.update(() => {
      const selection = getSelection();
      if ($isRangeSelection(selection)) {
        selection.formatText(format);
      }
    });
  };

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const formatList = (listType: 'bullet' | 'number') => {
    editor.update(() => {
      const selection = getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createListNode(listType));
      }
    });
  };

  return (
    <div className="border-b border-gray-200 p-2 flex flex-wrap gap-1">
      <button
        type="button"
        onClick={() => formatText('bold')}
        className="p-2 rounded hover:bg-gray-100 transition-colors"
        title="Bold"
      >
        <i className="ri-bold text-sm"></i>
      </button>
      <button
        type="button"
        onClick={() => formatText('italic')}
        className="p-2 rounded hover:bg-gray-100 transition-colors"
        title="Italic"
      >
        <i className="ri-italic text-sm"></i>
      </button>
      <button
        type="button"
        onClick={() => formatText('underline')}
        className="p-2 rounded hover:bg-gray-100 transition-colors"
        title="Underline"
      >
        <i className="ri-underline text-sm"></i>
      </button>
      
      <div className="w-px h-6 bg-gray-300 mx-1"></div>
      
      <button
        type="button"
        onClick={() => formatHeading('h1')}
        className="p-2 rounded hover:bg-gray-100 transition-colors text-sm font-bold"
        title="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h2')}
        className="p-2 rounded hover:bg-gray-100 transition-colors text-sm font-bold"
        title="Heading 2"
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h3')}
        className="p-2 rounded hover:bg-gray-100 transition-colors text-sm font-bold"
        title="Heading 3"
      >
        H3
      </button>
      
      <div className="w-px h-6 bg-gray-300 mx-1"></div>
      
      <button
        type="button"
        onClick={() => formatList('bullet')}
        className="p-2 rounded hover:bg-gray-100 transition-colors"
        title="Bullet List"
      >
        <i className="ri-list-unordered text-sm"></i>
      </button>
      <button
        type="button"
        onClick={() => formatList('number')}
        className="p-2 rounded hover:bg-gray-100 transition-colors"
        title="Numbered List"
      >
        <i className="ri-list-ordered text-sm"></i>
      </button>
      <button
        type="button"
        onClick={formatQuote}
        className="p-2 rounded hover:bg-gray-100 transition-colors"
        title="Quote"
      >
        <i className="ri-double-quotes-l text-sm"></i>
      </button>
    </div>
  );
}

function OnChangeHandler({ onChange }: { onChange: (value: string, html?: string) => void }) {
  const [editor] = useLexicalComposerContext();

  const handleChange = (editorState: any) => {
    editorState.read(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();

      // Generate HTML
      try {
        const htmlContent = $generateHtmlFromNodes(editor, null);
        onChange(textContent, htmlContent || textContent);
      } catch (error) {
        console.error('Error generating HTML:', error);
        onChange(textContent, textContent);
      }
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

export default function RichTextEditor({ value, onChange, placeholder = "Start writing...", className = "" }: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'RichTextEditor',
    theme,
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
    ],
  };

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden ${className}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable 
                className="min-h-[200px] p-4 outline-none resize-none"
                style={{ caretColor: 'rgb(5, 5, 5)' }}
              />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-gray-400 pointer-events-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangeHandler onChange={onChange} />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
        </div>
      </LexicalComposer>
    </div>
  );
}
